import React, { useState, useEffect, Fragment, useMemo } from "react";

import { Col } from "antd";
import get from "lodash/get";
import reduce from "lodash/reduce";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import HtmlViewer from "shared/components/HtmlViewer";
import Input from "shared/components/Input";
import Space from "shared/components/Space";
import { NAME_SPACES } from "shared/locals/constants";
import { DOC_TYPES } from "utils/constants";
import { mapingFormFields } from "utils/helpers/input";

import "./style.scss";

const Component = ({ forms, statusInfo, onChange }) => {
  const { t } = useTranslation(NAME_SPACES.HOME);
  const LABELS = t("F001", { returnObjects: true });
  const [statusComment, setStatusComment] = useState("");
  const [fieldValue, setFieldValue] = useState({});
  const documentation = useMemo(
    () => get(statusInfo, "documentation"),
    [statusInfo],
  );

  useEffect(() => {
    const fieldsItemList = reduce(
      fieldValue,
      (result, value) => [...result, ...Object.values(value)],
      [],
    );
    onChange({
      fieldsItemList,
      statusComment,
    });
  }, [statusComment, fieldValue]);

  useEffect(() => {
    const fieldsValues = forms.reduce((result, form, index) => {
      const fields = form.fieldsList.reduce(
        (_result, _field) => ({
          ..._result,
          [_field.name]: {
            name: _field.name,
            location: _field.location,
            value: "",
          },
        }),
        {},
      );
      return {
        ...result,
        [index]: {
          ...result[index],
          ...fields,
        },
      };
    }, {});
    setFieldValue(fieldsValues);
  }, [forms]);

  const _onChange = (index) => (field) => (value) => {
    const newValues = { ...fieldValue };
    newValues[index][field].value = value;
    setFieldValue(newValues);
  };

  return (
    <div className={"cancel"}>
      <p className={"title"}>{statusInfo.desc}</p>
      {documentation &&
        documentation.url &&
        (documentation.docType === DOC_TYPES.PDF ? (
          <Button
            text={"PDF"}
            custom="button-pdf"
            variant="BORDERED"
            onClick={() => window.open(documentation.url)}
          />
        ) : documentation.docType === DOC_TYPES.HTML ? (
          <HtmlViewer
            url={documentation.url}
            className={"documentation-form"}
          />
        ) : null)}
      <Space h={32} />
      {forms.map((_item, index) => (
        <Col key={`form-fields:${_item.name}/${index}`}>
          <p className={"form-name"}>{_item.name}</p>
          {_item.fieldsList.map((field) => (
            <Fragment key={`form-field:${_item.name}/${index}/${field.name}`}>
              <Input
                value={get(fieldValue, `${index}.${field.name}.value`)}
                {...mapingFormFields[field.type](
                  field,
                  _onChange(index)(field.name),
                )}
              />
              <Space h={16} />
            </Fragment>
          ))}
        </Col>
      ))}
      <Input
        mode="textArea"
        rows={4}
        value={statusComment}
        placeholder={LABELS.COMMENT}
        onChange={(e) => setStatusComment(e.target.value)}
      />
    </div>
  );
};

Component.propTypes = {
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      fieldsList: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
          location: PropTypes.string.isRequired,
        }),
      ),
    }),
  ).isRequired,
  statusInfo: PropTypes.shape({
    desc: PropTypes.string,
    documentation: PropTypes.shape({
      url: PropTypes.string.isRequired,
      docType: PropTypes.string.isRequired,
    }),
  }).isRequired,
  onChange: PropTypes.bool.isRequired,
};
export default Component;
